import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles,
  Send,
  Bot,
  User,
  Pill,
  TrendingUp,
  AlertTriangle,
  Package,
  RotateCcw,
  Settings as SettingsIcon,
  AlertCircle,
} from 'lucide-react';
import { clsx } from 'clsx';
import api from '../config/api';

const suggestedPrompts = [
  { icon: Pill,          label: 'What medicines are low on stock?',          color: 'bg-red-100 text-red-600' },
  { icon: TrendingUp,    label: "Show today's sales summary",               color: 'bg-blue-100 text-blue-600' },
  { icon: AlertTriangle, label: 'Which medicines are expiring soon?',        color: 'bg-amber-100 text-amber-600' },
  { icon: Package,       label: 'Recommend restock quantities for low items', color: 'bg-purple-100 text-purple-600' },
];

function formatMarkdown(text) {
  if (!text) return '';
  let formatted = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Bold **text**
  formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  // Italic *text*
  formatted = formatted.replace(/\*(.*?)\*/g, '<em>$1</em>');
  // Headers ### Header
  formatted = formatted.replace(/^### (.*?)$/gm, '<h4 class="font-semibold text-gray-900 mt-2 mb-1">$1</h4>');
  formatted = formatted.replace(/^## (.*?)$/gm, '<h3 class="font-bold text-gray-900 mt-3 mb-1 text-base">$1</h3>');
  // Bullet lists (- or • or *)
  formatted = formatted.replace(/^[\s]*[•\-\*]\s+(.*?)$/gm, '<li class="ml-4 list-disc">$1</li>');
  // Numbered lists
  formatted = formatted.replace(/^[\s]*(\d+)\.\s+(.*?)$/gm, '<li class="ml-4 list-decimal"><strong>$1.</strong> $2</li>');
  // Line breaks
  formatted = formatted.replace(/\n/g, '<br/>');

  return formatted;
}

export default function AIAssistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [activeModel, setActiveModel] = useState('Google Gemini');
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    api.get('/settings')
      .then((res) => {
        const data = res?.settings || res;
        if (data?.gemini_model) {
          setActiveModel(data.gemini_model);
        }
      })
      .catch(() => {});
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const handleSend = async (text = input) => {
    const promptText = typeof text === 'string' ? text.trim() : input.trim();
    if (!promptText || isTyping) return;

    const userMsg = { id: Date.now(), role: 'user', content: promptText };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsTyping(true);
    setApiError(null);

    // Build history for multi-turn context (only previous messages)
    const history = messages.map((m) => ({
      role: m.role === 'user' ? 'user' : 'model',
      content: m.content,
    }));

    try {
      const res = await api.post('/ai/chat', {
        message: promptText,
        history: history,
      });

      if (res?.model) {
        setActiveModel(res.model);
      }

      const botReply = res?.reply || 'No response received.';
      const botMsg = {
        id: Date.now() + 1,
        role: 'bot',
        content: botReply,
      };
      setMessages((prev) => [...prev, botMsg]);
    } catch (err) {
      const errorMessage = err?.message || 'Failed to get response from AI Assistant.';
      setApiError(errorMessage);

      const errorMsg = {
        id: Date.now() + 1,
        role: 'bot',
        isError: true,
        content: `⚠️ **Unable to generate response:**\n\n${errorMessage}\n\n*Please ensure your Google Gemini API Key is set in Settings.*`,
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setIsTyping(false);
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 50);
    }
  };

  const handleClear = () => {
    setMessages([]);
    setApiError(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex h-[calc(100vh-8rem)] flex-col rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100">
            <Sparkles className="h-5 w-5 text-emerald-600" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold text-gray-900">AI Pharmacy Assistant</h2>
              <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 border border-emerald-200">
                {activeModel}
              </span>
            </div>
            <p className="text-xs text-gray-500">Live inventory analytics, sales insights & pharmaceutical advice</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {messages.length > 0 && (
            <button
              onClick={handleClear}
              className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
              title="Clear conversation"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              <span>Clear Chat</span>
            </button>
          )}

          <Link
            to="/settings"
            className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
            title="Configure Gemini API Key"
          >
            <SettingsIcon className="h-3.5 w-3.5" />
            <span>AI Settings</span>
          </Link>

          <div className="flex items-center gap-1.5 pl-2 border-l border-gray-200">
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-xs font-medium text-emerald-600">Online</span>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center py-6">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-100 mb-4">
              <Bot className="h-8 w-8 text-emerald-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">How can I assist your pharmacy today?</h3>
            <p className="mt-1 max-w-md text-center text-sm text-gray-500">
              Ask about low stock, upcoming expiries, sales summaries, restock advice, or drug information.
            </p>

            {/* Suggested prompts */}
            <div className="mt-8 grid w-full max-w-xl grid-cols-1 gap-3 sm:grid-cols-2">
              {suggestedPrompts.map((prompt) => (
                <button
                  key={prompt.label}
                  onClick={() => handleSend(prompt.label)}
                  className="flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-4 text-left transition-all hover:border-emerald-300 hover:shadow-md active:scale-[0.98]"
                >
                  <div className={clsx('flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg', prompt.color)}>
                    <prompt.icon className="h-4 w-4" />
                  </div>
                  <span className="text-sm font-medium text-gray-700">{prompt.label}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4 max-w-4xl mx-auto">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={clsx('flex gap-3', msg.role === 'user' ? 'justify-end' : 'justify-start')}
              >
                {msg.role === 'bot' && (
                  <div className={clsx(
                    'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg',
                    msg.isError ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-600'
                  )}>
                    {msg.isError ? <AlertCircle className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                  </div>
                )}
                <div
                  className={clsx(
                    'max-w-[80%] rounded-2xl px-5 py-3.5 text-sm leading-relaxed shadow-sm',
                    msg.role === 'user'
                      ? 'bg-emerald-500 text-white rounded-br-md'
                      : msg.isError
                      ? 'bg-red-50 border border-red-200 text-red-900 rounded-bl-md'
                      : 'bg-gray-100 border border-gray-200/70 text-gray-900 rounded-bl-md'
                  )}
                >
                  <div
                    className="whitespace-pre-wrap break-words"
                    dangerouslySetInnerHTML={{
                      __html: formatMarkdown(msg.content),
                    }}
                  />
                  {msg.isError && (
                    <div className="mt-3 pt-2 border-t border-red-200">
                      <Link
                        to="/settings"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-700 hover:text-red-800 hover:underline"
                      >
                        <SettingsIcon className="h-3.5 w-3.5" />
                        <span>Go to Settings to add Gemini API Key &rarr;</span>
                      </Link>
                    </div>
                  )}
                </div>
                {msg.role === 'user' && (
                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-500 text-white">
                    <User className="h-4 w-4" />
                  </div>
                )}
              </div>
            ))}

            {/* Typing indicator */}
            {isTyping && (
              <div className="flex gap-3 justify-start">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600">
                  <Bot className="h-4 w-4" />
                </div>
                <div className="rounded-2xl rounded-bl-md bg-gray-100 border border-gray-200/70 px-5 py-3.5">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 font-medium mr-1">Gemini is analyzing...</span>
                    <span className="h-2 w-2 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="h-2 w-2 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="h-2 w-2 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input */}
      <div className="border-t border-gray-100 bg-white px-6 py-4">
        <div className="flex items-end gap-3 max-w-4xl mx-auto">
          <div className="relative flex-1">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about inventory, sales analytics, batch expiries, restock recommendations..."
              rows={1}
              className="w-full resize-none rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 placeholder-gray-400 transition-all focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
            />
          </div>
          <button
            onClick={() => handleSend()}
            disabled={!input.trim() || isTyping}
            className={clsx(
              'flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl transition-all',
              input.trim() && !isTyping
                ? 'bg-emerald-500 text-white shadow-sm hover:bg-emerald-600 active:scale-95'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            )}
            title="Send Message"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
